/**
 * Sprint 11 — Reusable Email-link send for individual deployments.
 *
 * Surfaces (1) the "Email link to <client>" button + (2) a relative
 * last-sent indicator next to it (e.g. "Last sent 3 days ago"). Opens
 * the same two-step preview modal pattern used in the New Deployment
 * wizard.
 *
 * Used from:
 *   - NewDeployment wizard's generated-link panel (creation flow)
 *   - DeploymentDetail header (re-engagement flow — coach sends Monday,
 *     comes back Friday, clicks here to resend without re-creating)
 *
 * The backend endpoints (`GET .../send-link/preview`,
 * `POST .../send-link`) are idempotent — they just write a new
 * audit_log row + bump invitation_link_sent_at. Same respondent token
 * across re-sends.
 */
import React from "react";
import { api, formatApiError } from "../lib/api";

function relativeFromNow(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (isNaN(then)) return null;
  const diffMs = Date.now() - then;
  if (diffMs < 0) return "just now";
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return days === 1 ? "yesterday" : `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
  return new Date(iso).toLocaleDateString();
}

export default function SendLinkButton({
  deploymentId,
  clientName,
  lastSentAt,
  onSent,
  variant = "primary", // "primary" (filled blue) | "header" (bordered, sits in header strip)
}) {
  const [preview, setPreview] = React.useState(null);
  const [sending, setSending] = React.useState(false);
  const [error, setError] = React.useState("");
  // Local override so the relative text updates immediately on a
  // successful send without waiting for the parent to refetch.
  const [localSentAt, setLocalSentAt] = React.useState(null);

  const effectiveSentAt = localSentAt || lastSentAt;
  const relative = relativeFromNow(effectiveSentAt);

  async function openPreview() {
    setError("");
    try {
      const { data } = await api.get(`/api/deployments/${deploymentId}/send-link/preview`);
      setPreview(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  }

  async function confirmSend() {
    setSending(true);
    setError("");
    try {
      const { data } = await api.post(`/api/deployments/${deploymentId}/send-link`, {});
      if (data.ok) {
        setLocalSentAt(data.sent_at);
        setPreview(null);
        if (onSent) onSent(data.sent_at);
      } else {
        setError(data.error || "Send failed.");
      }
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSending(false);
    }
  }

  const isHeader = variant === "header";
  const btnLabel = effectiveSentAt
    ? `Re-send link to ${clientName || "client"}`
    : `Email link to ${clientName || "client"}`;

  return (
    <>
      <div className="flex items-center gap-3 flex-wrap" data-testid="send-link-control">
        <button
          type="button"
          onClick={openPreview}
          data-testid="open-send-link"
          style={{
            fontFamily: "Oswald",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            fontSize: "11px",
            color: isHeader ? "#1A1A1A" : "#FBFAF7",
            background: isHeader ? "transparent" : "#2563EB",
            border: isHeader ? "1px solid #2563EB" : 0,
            padding: isHeader ? "8px 14px" : "10px 16px",
            cursor: "pointer",
          }}
        >
          {btnLabel}
        </button>
        {relative && (
          <span
            className="text-xs text-gray-600"
            data-testid="last-sent-indicator"
            title={effectiveSentAt ? new Date(effectiveSentAt).toLocaleString() : undefined}
          >
            Last sent {relative}
          </span>
        )}
      </div>
      {error && (
        <div className="text-xs mt-2" data-testid="send-link-error" style={{ color: "#AD393E" }}>
          {error}
        </div>
      )}

      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-12"
          style={{ background: "rgba(26,26,26,0.4)" }}
          data-testid="send-link-modal"
          onClick={() => !sending && setPreview(null)}
        >
          <div
            className="bg-paper p-8 w-full max-w-xl"
            style={{ border: "1px solid #DDDDDD" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="eyebrow mb-2" style={{ color: "#2563EB" }}>Send preview</div>
            <h3 className="text-xl mb-3" style={{ fontFamily: "Oswald", fontWeight: 700, color: "#1A1A1A" }}>
              {effectiveSentAt ? "Re-send" : "Email"} link to {preview.client_name}
            </h3>
            <p className="text-xs text-gray-600 mb-4">
              To: <strong>{preview.to}</strong> · From: noreply@resilientteams.io
            </p>
            <div className="mb-4">
              <div className="label">Subject</div>
              <div
                className="text-sm py-2 px-3"
                style={{ background: "white", border: "1px solid #E0E0E0" }}
                data-testid="send-link-subject"
              >
                {preview.subject}
              </div>
            </div>
            <div className="mb-4">
              <div className="label">Body</div>
              <pre
                className="text-sm py-3 px-3 whitespace-pre-wrap leading-relaxed"
                style={{
                  background: "white", border: "1px solid #E0E0E0",
                  fontFamily: "inherit", maxHeight: "240px", overflowY: "auto",
                }}
                data-testid="send-link-body"
              >{preview.body}</pre>
            </div>
            {preview.previously_sent_at && (
              <p className="text-xs mb-3" style={{ color: "#7F7F7F" }}>
                Previously sent {new Date(preview.previously_sent_at).toLocaleString()} —
                re-sending uses the same survey link.
              </p>
            )}
            {!preview.email_configured && (
              <p className="text-xs mb-3" style={{ color: "#AD393E" }} data-testid="send-link-unconfigured">
                Email service is not configured on this environment.
              </p>
            )}
            <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setPreview(null)}
                disabled={sending}
                className="btn-quiet"
                data-testid="send-link-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmSend}
                disabled={sending || !preview.email_configured}
                data-testid="send-link-confirm"
                style={{
                  fontFamily: "Oswald", letterSpacing: "0.18em", textTransform: "uppercase",
                  fontSize: "11px", color: "#FBFAF7",
                  background: sending ? "#999898" : "#2563EB",
                  border: 0, padding: "10px 18px",
                  cursor: sending ? "wait" : "pointer",
                }}
              >
                {sending ? "Sending…" : "Confirm and send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
